import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  formattedDate,
  formattedTime,
} from "../../../components/formatted/FormattedDateAndTime";
import { enqueueWasteStatisticsMetricsForPeriod } from "../../../services/wasteStatisticsMetrics";

/**
 * Admin-only header action: last update and refresh in one button.
 */
const StatisticsMetricsRefreshBar = ({
  lastUpdate,
  periodHistoryStart,
  periodHistoryStop,
  keywordFilter,
  onRefreshQueued,
  onExportWeeklyReport,
  canExportWeeklyReport = true,
  isExportingWeeklyReport = false,
}) => {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const buttonLabel = isRefreshing
    ? t("statistics.metricsRefreshing")
    : lastUpdate
      ? t("statistics.metricsRefreshCompact", {
          date: formattedDate(lastUpdate, t, true, false, false).replace(
            /\.$/,
            "",
          ),
          time: formattedTime(lastUpdate),
        })
      : t("statistics.metricsRefresh");

  const handleRefresh = async () => {
    if (isRefreshing || !periodHistoryStart || !periodHistoryStop) return;
    setIsRefreshing(true);
    try {
      const response = await enqueueWasteStatisticsMetricsForPeriod({
        start: periodHistoryStart,
        stop: periodHistoryStop,
        keywordFilter: keywordFilter?.value || [],
      });
      const enqueued = response?.data?.enqueued ?? 0;
      toast.success(t("statistics.metricsRefreshQueued", { count: enqueued }), {
        className: "custom-toast",
      });
      if (typeof onRefreshQueued === "function") {
        onRefreshQueued(response?.data);
      }
    } catch (error) {
      console.error("Metrics refresh failed:", error);
      toast.error(t("statistics.metricsRefreshError"), {
        className: "custom-toast",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      {typeof onExportWeeklyReport === "function" ? (
        <button
          type="button"
          className="page-header-pill statistics-refresh"
          onClick={onExportWeeklyReport}
          disabled={
            isExportingWeeklyReport || !periodHistoryStart || !periodHistoryStop
          }
          title={
            canExportWeeklyReport ? undefined : t("weeklyReport.needOneSite")
          }
        >
          <i className="fas fa-file-pdf" aria-hidden />
          <span>
            {isExportingWeeklyReport
              ? t("weeklyReport.exporting")
              : t("weeklyReport.export")}
          </span>
        </button>
      ) : null}
      <button
        type="button"
        className="page-header-pill statistics-refresh"
        onClick={handleRefresh}
        disabled={isRefreshing || !periodHistoryStart || !periodHistoryStop}
      >
        <i className="fas fa-sync-alt" aria-hidden />
        <span>{buttonLabel}</span>
      </button>
    </div>
  );
};

export default StatisticsMetricsRefreshBar;
